import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Layout } from '../components/layout';

/**
 * ProtectedRoute component that guards routes requiring authentication.
 * Redirects unauthenticated users to the login page.
 * Redirects users who must change password to the change-password page.
 * Preserves the intended destination for redirect after login.
 * Wraps authenticated routes in the Layout component.
 * 
 * Requirements: 1.1 - Redirect unauthenticated users to login page
 * Requirements: 8.2 - Force password change on first login
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuthStore();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Checking authentication...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to change-password if user must change their password
  // (but don't redirect if already on change-password page)
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Render child routes wrapped in Layout
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}
